/**
 * Captura um print da tela via a API nativa getDisplayMedia (dispara o prompt de
 * permissão/seleção do navegador). Pega um único frame, encerra o stream na hora
 * e devolve um Blob WebP (downscale p/ caber no limite de anexo do backend).
 *
 * Usa a via do <video> (portátil; ImageCapture não existe em todos os browsers).
 * Antes de chamar, a UI do widget deve estar OCULTA — senão o próprio botão
 * entra no print.
 */
export async function capturarTela(maxLargura = 1920): Promise<Blob> {
  // preferCurrentTab/selfBrowserSurface reduzem o atrito no Chrome; não estão nos
  // tipos padrão do TS, então estendemos as opções via objeto solto.
  const opts: DisplayMediaStreamOptions & Record<string, unknown> = {
    video: true,
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: "include",
  };
  const stream = await navigator.mediaDevices.getDisplayMedia(opts);
  try {
    const video = await pegarFrame(stream);
    return await desenharBlob(video, maxLargura);
  } finally {
    for (const t of stream.getTracks()) t.stop();
  }
}

/**
 * getDisplayMedia (captura de tela) só existe em desktop — no mobile (iOS Safari
 * sobretudo) é `undefined`. Serve de gate p/ cair no fallback por arquivo.
 */
export function suportaCapturaTela(): boolean {
  return typeof navigator?.mediaDevices?.getDisplayMedia === "function";
}

/**
 * Abre o seletor nativo de arquivo (`<input type=file>`) e resolve com o escolhido, ou
 * `null` se cancelar. `accept` vazio = qualquer tipo (o anexo do report, DEV-211); com
 * `image/*` o SO oferece galeria/câmera, que é o fallback de print do mobile.
 *
 * O input é criado, clicado e removido: um `<input>` permanente dentro do Shadow DOM não
 * ajudaria (o seletor é do SO) e ficaria de pé em toda tela.
 */
export function escolherArquivo(accept = ""): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    if (accept !== "") input.accept = accept;
    input.style.position = "fixed";
    input.style.left = "-9999px";
    let resolvido = false;
    const finalizar = (v: File | null): void => {
      if (resolvido) return;
      resolvido = true;
      input.remove();
      resolve(v);
    };
    input.addEventListener("change", () => finalizar(input.files?.[0] ?? null));
    input.addEventListener("cancel", () => finalizar(null)); // browsers modernos
    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Fallback do mobile: o seletor nativo já filtrado em imagem, onde a pessoa escolhe um
 * screenshot tirado no aparelho ou uma foto. NÃO forçamos `capture` p/ não pular direto
 * para a câmera.
 */
export function escolherImagem(): Promise<File | null> {
  return escolherArquivo("image/*");
}

/**
 * Normaliza uma imagem escolhida pelo usuário (fallback mobile) para o MESMO
 * formato da captura de tela: WebP com downscale p/ caber no limite de anexo do
 * backend. Assim o resto do fluxo (anotar/enviar) trata os dois caminhos igual.
 */
export async function normalizarImagem(file: Blob, maxLargura = 1920): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  try {
    const escala = Math.min(1, maxLargura / Math.max(1, bmp.width));
    const cw = Math.max(1, Math.round(bmp.width * escala));
    const ch = Math.max(1, Math.round(bmp.height * escala));

    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D indisponível.");
    ctx.drawImage(bmp, 0, 0, cw, ch);
    return await paraWebp(canvas);
  } finally {
    bmp.close();
  }
}

async function pegarFrame(stream: MediaStream): Promise<HTMLVideoElement> {
  const v = document.createElement("video");
  v.srcObject = stream;
  v.muted = true;
  v.playsInline = true;
  await v.play().catch(() => undefined);
  await new Promise<void>((r) => {
    if (v.readyState >= 2) r();
    else v.onloadeddata = () => r();
  });
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
  return v;
}

async function desenharBlob(v: HTMLVideoElement, maxLargura: number): Promise<Blob> {
  const w = v.videoWidth || window.innerWidth;
  const h = v.videoHeight || window.innerHeight;
  const escala = Math.min(1, maxLargura / Math.max(1, w));
  const cw = Math.max(1, Math.round(w * escala));
  const ch = Math.max(1, Math.round(h * escala));

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D indisponível.");
  ctx.drawImage(v, 0, 0, cw, ch);
  return await paraWebp(canvas);
}

/** O formato de saída de TODO caminho de imagem do widget (captura, arquivo escolhido,
    rasterização): WebP com a mesma qualidade, para que o resto do fluxo não saiba de onde
    a imagem veio. Exportado por isso — `rasterizar.ts` termina aqui também. */
export function paraWebp(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Falha ao gerar a imagem."))),
      "image/webp",
      0.85,
    );
  });
}
